import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { filter } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { Hotel, User } from '../shared/model/index';
import { environment } from "../../environments/environment";
import { SessionService } from './session.service';
import { NavbarService } from './navbar.service';
import { MenusState } from '../states/menus/menus.state';
import { MenuItem } from '../states/menus/menus.model';

// Tipado seguro para Window
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  private currentIdProperty: string = '';
  private isBrowser: boolean;
  private globalParams: any = {};
  private userDimensionsReady = false;
  private pendingPageView: string | null = null;
  private lastSentPageView: { url: string; time: number } = { url: '', time: 0 };
  private initializedIds = new Set<string>();

  constructor(
    private router: Router,
    private sessionService: SessionService,
    private store: Store,
    private navbarService: NavbarService,
    @Inject(PLATFORM_ID) platformId: Object,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser) {
      this.initRouterTracking();
    }
  }

  private initRouterTracking() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (typeof window.gtag !== 'function') return;
      const url = event.urlAfterRedirects;
      const path = url.split('?')[0] || url;
      const menus = this.store.selectSnapshot(MenusState.currentMenuSidebar) || {};
      const activeMenu = this.getActiveMenu(menus, this.navbarService.getStatus());
      const match = this.findProductAndSection(activeMenu, path);
      const isPublic = !!this.currentIdProperty &&
        environment.googleAnalyticsGuest?.includes(this.currentIdProperty);

      if (this.userDimensionsReady || isPublic) {
        this.basicInfo(url, match?.product, match?.section);
      } else {
        // Buffer only the URL — match is recomputed at flush time when menu state is ready
        this.pendingPageView = url;
      }
    });
  }

  private getActiveMenu(menus: { sidebar?: MenuItem[]; configs?: MenuItem[]; managers?: MenuItem[] }, status: number): MenuItem[] {
    if (status === 1) return menus.configs || [];
    if (status === 2) return menus.managers || [];
    if (status === 3) {
      const sidebar = menus.sidebar || [];
      const corporative = sidebar.find(item =>
        item.link === '/intercadena' || item.slug === 'corporative' || item.slug === 'interchain'
      );
      const children = corporative?.childrens;
      return (children?.length ? children : sidebar) as MenuItem[];
    }
    return menus.sidebar || [];
  }

  private findProductAndSection(sidebar: MenuItem[], urlPath: string): { product: string; section: string } | null {
    const norm = (p: string) => (p || '').replace(/\/$/, '');
    const path = norm(urlPath);
    // for specific urls, return the product and section
    const specificUrls: { [key: string]: { product: string; section: string } } = {
      '/dashboard': { product: 'home', section: 'home' },
      '/login': { product: 'login', section: 'login' },
    };

    if (specificUrls[path]) {
      return specificUrls[path];
    }

    if (!sidebar?.length) return null;
    const items: { item: MenuItem; product: string }[] = [];
    const push = (list: MenuItem[], productRoot: string) => {
      list.forEach(item => {
        items.push({ item, product: productRoot });
        if (item.childrens?.length) push(item.childrens, productRoot);
      });
    };
    sidebar.forEach(root => {
      const productRoot = root.title || root.slug || '';
      items.push({ item: root, product: productRoot });
      if (root.childrens?.length) push(root.childrens, productRoot);
    });
    const matched = items
      .filter(({ item }) => {
        const link = norm(item.link || '');
        return link && (path === link || path.startsWith(link + '/'));
      })
      .sort((a, b) => (b.item.link || '').length - (a.item.link || '').length)[0];
    if (!matched) return null;
    return {
      product: matched.product,
      section: matched.item.title || matched.item.slug || '',
    };
  }

  private matchFromIntercadenaPath(path: string): { product: string; section: string; subsection: string } | null {
    const segments = path.split('/').filter(Boolean);
    if (segments[0] !== 'intercadena' || segments.length < 2) return null;
    const section = segments[1];
    const subsectionRaw = segments[2] ?? '';
    const sectionLabel: Record<string, string> = { resumen: 'summary', ejecutivo: 'executive', propiedades: 'properties', tendencias: 'trends' };
    return {
      product: 'interchain',
      section: sectionLabel[section] || section,
      subsection: subsectionRaw ? (sectionLabel[subsectionRaw] || subsectionRaw) : '',
    };
  }

  public emitEvent(
    eventCategory: string,
    eventAction: string,
    eventLabel: string | null = '',
    eventValue: number | string | null = 0,
    additionalParams: any = {}
  ) {
    if (!this.isBrowser) return;

    if(!environment.production) console.log(`GA: ${eventCategory} - ${eventAction} - ${eventLabel}`, additionalParams);
    
    const params = {
      ...additionalParams
    };    
    
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventAction, {
        event_category: eventCategory,
        event_label: eventLabel,
        value: eventValue,
        ...params
      });
    }
  }

  basicInfo(url: string, product?: string, section?: string, subsection?: string) {
    if (!this.isBrowser) return;

    const now = Date.now();
    if (url === this.lastSentPageView.url && now - this.lastSentPageView.time < 1000) return;
    this.lastSentPageView = { url, time: now };

    const user = this.getCurrentUser();
    const customer = this.getCurrentCustomer();
    const isPublic = !!this.currentIdProperty &&
      environment.googleAnalyticsGuest?.includes(this.currentIdProperty);

    const shouldSendPublic = isPublic || !this.isProduction() || !user || (!user.isSuperAdmin && !user.isSuperAdmin());

    if (shouldSendPublic && isPublic) {
      this.sendPublicInfo(url, product, section, subsection);
    } else {
      this.sendInfo(customer, user, url, product, section, subsection);
    }
  }

  setGlobalDimensions(customer: Hotel, user?: any) {
    if (!this.isBrowser || typeof window.gtag !== 'function') return;

    const dimensions: Record<string, string> = {};
    
    if (customer) {
      dimensions.hotel_id = String(customer.id ?? '');
      dimensions.hotel_name = customer.name;
      dimensions.hotel_country = customer.country_code ?? '';
    }
    
    if (user) {
      dimensions.user_role = user.user_role?.name ?? '';
      dimensions.user_department = user.department?.code ?? '';
      dimensions.user_job_level = user.role?.code ?? '';
      dimensions.user_job_title = user.company_position ?? '';
      dimensions.user_language = this.languageIdToCode(user.language_id) || '';
      dimensions.is_cs_counterpart = user.cs_partner === true ? 'true' : 'false';
    }

    if (!Object.keys(dimensions).length) return;

    if (user?.id) {
      window.gtag('set', { user_id: String(user.id) });
    }
    window.gtag('set', 'user_properties', dimensions);
    this.globalParams = { ...this.globalParams, ...dimensions };

    if (user && !this.userDimensionsReady) {
      this.userDimensionsReady = true;
      if (this.pendingPageView) {
        const url = this.pendingPageView;
        this.pendingPageView = null;
        const path = url.split('?')[0] || url;
        const menus = this.store.selectSnapshot(MenusState.currentMenuSidebar) || {};
        const activeMenu = this.getActiveMenu(menus, this.navbarService.getStatus());
        const match = this.findProductAndSection(activeMenu, path);
        this.basicInfo(url, match?.product, match?.section);
      }
    }
  }

  setPublicDimensions(customerName: string, guestId: any) {
    if (!this.isBrowser || typeof window.gtag !== 'function') return;
    if (customerName) window.gtag('set', { 'customer_name': customerName });
    if (guestId) window.gtag('set', { 'guest_id': guestId });
  }

  sendInfo(customer: Hotel, user: User, url: string, product?: string, section?: string, subsection?: string) {
    if (!this.isBrowser || typeof window.gtag !== 'function') return;
    const params: Record<string, string> = { page_title: url };
    if (product) params.product = product;
    if (section) params.section = section;
    if (subsection) params.subsection = subsection;
    const allparams = {
      ...params
    };    
    window.gtag('event', 'page_view', allparams);
  }

  sendPublicInfo(url: string, product?: string, section?: string, subsection?: string) {
    if (!this.isBrowser || typeof window.gtag !== 'function') return;

    const customer = this.sessionService.getCurrentHotelPS();
    const guest = this.sessionService.getCurrentGuest();

    if (customer && guest && guest.id !== 0) {
      this.setPublicDimensions(customer.name, guest.id);
      const params: Record<string, string> = { page_title: url };
      if (product) params.product = product;
      if (section) params.section = section;
      if (subsection) params.subsection = subsection;
      const allparams = {
        ...params
      };
      window.gtag('event', 'page_view', allparams);
    }
  }

  appendGaTrackingCode(idGA: string, user_id?: any) {
    if (!this.isBrowser) return;

    const options: any = { send_page_view: false };
    if (user_id) options['user_id'] = user_id;

    this.currentIdProperty = idGA.split('-')[1] || '';

    window.dataLayer = window.dataLayer || [];

    if (typeof window.gtag === 'undefined') {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }

    // Calling gtag('config', ...) on an already-loaded tracker sends an automatic
    // page_view even with send_page_view:false. Guard with a per-ID flag.
    if (this.initializedIds.has(idGA)) return;
    this.initializedIds.add(idGA);

    // Standard GA4 pattern: queue js+config synchronously BEFORE the script loads.
    // gtag.js processes the dataLayer queue when it initializes, so this guarantees
    // the correct order: js → config → set user_properties (called later).
    window.gtag('js', new Date());
    window.gtag('config', idGA, options);

    const scriptId = 'ga-gtag-script';
    if (!this.document.getElementById(scriptId)) {
      const script = this.document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${idGA}`;
      this.document.head.appendChild(script);
    }
  }

  public sendInfoSurvey(guestId: any, hotelName: string) {
    if (!this.isBrowser) return;
    if (!this.isProduction() || !hotelName || !guestId || guestId === 1) return;

    const publicSurveyCode = 'G-E309GY39LS';
    this.appendGaTrackingCode(publicSurveyCode, guestId);
    this.setPublicDimensions(hotelName, guestId);

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view');
    }
  }
    
  // ... Métodos helpers
   getCurrentUser():User{
    return this.sessionService.getCurrentUser();
  }

  getCurrentCustomer():Hotel{
    return this.sessionService.getCurrentCustomer();
  }

  isProduction(){
    return `${environment.production}` === 'true';
  }

  private languageIdToCode(languageId: number | undefined): string {
    if (languageId == null) return '';
    const map: Record<number, string> = { 1: 'es', 2: 'en', 7: 'pt' };
    return map[languageId] ?? String(languageId);
  }
}